import React, { useEffect, useMemo, useState } from "react";
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
} from "react-native";
import { useTranslation } from "react-i18next";
import Colors from "../../../../../constants/colors";
import { getOrFetchMapiSubdivisions } from "../../../../../helper/mapiSubdivisionsCache";
import { useSessionStore } from "../../../../../../../state/src/useSessionStore";
import type { SessionState } from "../../../../../../../state/src/useSessionStore";
import RadioSelectedIcon from "../../../../../../../../assets/images/RadioSelectButton.svg";
import FlatListDataComponent from "../../../common/FlatListDataComponent";
import { capturePosthogEvent } from "../../../../../utils/analytics";

type StateItem = {
  code: string;
  name: string;
};

type MapiChooseStateStepProps = {
  onCountrySelected?: () => void;
  testID?: string | undefined;
};

export default function MapiChooseStateStep({
  onCountrySelected,
  testID,
}: MapiChooseStateStepProps) {
  const { t } = useTranslation();
  const [search, setSearch] = useState("");
  const [selectedState, setSelectedState] = useState<string | null>(null);
  const [states, setStates] = useState<StateItem[]>([]);
  const selectedDestinationCountryCode = useSessionStore(
    (s: SessionState) => s.selectedDestinationCountryCode
  );
  const setSelectedDestinationStateAbbr = useSessionStore(
    (s: SessionState) => s.setSelectedDestinationStateAbbr
  );
  const setSelectedDestinationStateName = useSessionStore(
    (s: SessionState) => s.setSelectedDestinationStateName
  );

  useEffect(() => {
    const fetchStates = async () => {
      if (!selectedDestinationCountryCode) return;
      try {
        const subdivisions = await getOrFetchMapiSubdivisions(
          selectedDestinationCountryCode
        );
        const mapped: StateItem[] = (subdivisions || []).map((s: any) => ({
          code: s.code,
          name: s.name,
        }));
        setStates(mapped);
      } catch (e) {
        console.error("Failed to load states", e);
      }
    };
    fetchStates();
  }, [selectedDestinationCountryCode]);

  const filteredState: StateItem[] = useMemo(() => {
    const q = search.toLowerCase();
    return states.filter((s) =>
      (s.name || "").toLowerCase().includes(q)
    );
  }, [states, search]);

  const onSelectState = (item: StateItem) => {
    setSelectedState(item.name);
    setSelectedDestinationStateName(item.name);
    setSelectedDestinationStateAbbr(item.code);
    setTimeout(() => {
      onCountrySelected?.();
    }, 200);
    capturePosthogEvent("Mapi Send Flow - State Capture Step", {
      "data-mg-state": item.name,
      "data-mg-state-code": item.code,
    });
  };

  return (
    <View style={styles.container} testID={testID}>
      <FlatListDataComponent
        title={t("STATE_STEP_TITLE")}
        subTitle={t("CHOOSE_WHAT_STATE_YOU_ARE_SENDING_TO")}
        searchPlaceHolder={t("COUNTRY_STEP_SEARCH_PLACEHOLDER")}
        searchValue={search}
        onSearchChangeText={setSearch}
        filteredData={filteredState}
        searchTestID="mapiStateSearchInput"
        flatListTestID="mapiStateList"
        renderItem={(item: StateItem) => (
          <TouchableOpacity
            testID="mapiStateSelected"
            style={styles.countryRow}
            onPress={() => onSelectState(item)}
          >
            <Text style={styles.countryName}>
              {item.name} ({item.code})
            </Text>
            <View style={styles.radioButton}>
              {selectedState === item.name && (
                <RadioSelectedIcon width={20} height={20} />
              )}
            </View>
          </TouchableOpacity>
        )}
      />
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    paddingHorizontal: 24,
    backgroundColor: Colors.white,
  },
  countryRow: {
    backgroundColor: Colors.white,
    flexDirection: "row",
    alignItems: "center",
    paddingVertical: 16,
    borderBottomWidth: 0,
    justifyContent: "space-between",
  },
  countryName: {
    fontSize: 16,
    fontWeight: "400",
    fontFamily: "Rubik",
    lineHeight: 20,
    color: Colors.titleTextColor,
  },
  radioButton: {
    width: 20,
    height: 20,
    borderRadius: 10,
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: Colors.radioButtonFilled,
  },
});
